<script setup lang="ts">
import type { ButtonProps } from 'naive-ui'
import type { Task } from '~/types/task.type'
import { TimeEntryConfirmStartModal, UIcon } from '#components'
import { tableActionsIsInsideDropdownInjectionKey } from '~/injections'

const props = defineProps({
  taskId: {
    type: Number as PropType<Task['id']>,
    required: true,
  },
  buttonProps: {
    type: Object as PropType<ButtonProps & { onMousedown?: (event: PointerEvent) => void }>,
    default: () => ({}),
  },
  showButtonLabel: {
    type: Boolean,
    default: false,
  },
})

const overlay = useOverlay()
const timeTracker = useTimeTracker()
const { isRunning, activeTimeEntry } = storeToRefs(timeTracker)

const timeTrackerLoading = ref(false)

const isRunningForThisTask = computed(() => {
  return isRunning.value && activeTimeEntry.value?.task.id === props.taskId
})

const icon = computed(() => {
  return isRunningForThisTask.value ? 'i-ic-outline-stop-circle' : 'i-ic-outline-play-circle'
})

const tooltip = computed(() => {
  if (isRunningForThisTask.value)
    return 'Laufenden Zeiteintrag abschließen'

  else if (isRunning.value)
    return 'Tracker stoppen und für diese Aufgabe starten'

  else
    return 'Tracker für diese Aufgabe starten'
})

const timeEntryConfirmStartModal = overlay.create(TimeEntryConfirmStartModal)

async function openTimeTrackerDialog() {
  if (!activeTimeEntry.value)
    return
  timeEntryConfirmStartModal.open({
    title: 'Wirklich einen neuen Zeiteintrag starten?',
    timeEntry: activeTimeEntry.value,
    onClose: async (isConfirmed) => {
      if (isConfirmed) {
        timeTrackerLoading.value = true
        await timeTracker.stop()
        await timeTracker.start(props.taskId)
        timeTrackerLoading.value = false
      }
    },
  })
}

async function onClick() {
  if (isRunningForThisTask.value) {
    timeTrackerLoading.value = true
    await timeTracker.stop()
    timeTrackerLoading.value = false
  }
  else if (isRunning.value) {
    await openTimeTrackerDialog()
  }
  else {
    timeTrackerLoading.value = true
    await timeTracker.start(props.taskId)
    timeTrackerLoading.value = false
  }
}

const isInsideDropdown = inject(tableActionsIsInsideDropdownInjectionKey, false)
</script>

<template>
  <NTooltip :disabled="isInsideDropdown">
    <template #trigger>
      <NButton
        v-bind="buttonProps"
        :quaternary="isInsideDropdown"
        :loading="timeTrackerLoading"
        :class="{ '!justify-start': isInsideDropdown }"
        @click.stop="onClick"
      >
        <template #icon>
          <UIcon :name="icon" />
        </template>
        <span v-if="isInsideDropdown || showButtonLabel">{{ tooltip }}</span>
      </NButton>
    </template>
    {{ tooltip }}
  </NTooltip>
</template>
